import { Component, Inject, ViewEncapsulation, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import * as XLSX from 'xlsx';

import { CustomMessageService } from '../../components/custom-message/custom-message.service';

interface ColumnStructure {
  columnName: string;
  columnKey:  string;
  type:       string;
}

interface DialogConfig {
  structure?: ColumnStructure[];
}

interface MappingEntry {
  name: string;
  type: string;
}

@Component({
  selector: 'app-upload-file-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule],
  templateUrl: './upload-file-dialog.component.html',
  styleUrls:   ['./upload-file-dialog.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
})
export class UploadFileDialogComponent {

  public dialogRef = inject<MatDialogRef<UploadFileDialogComponent>>(MatDialogRef);
  private customMessage = inject(CustomMessageService);

  // Inicializadas para satisfacer strictPropertyInitialization
  file:           File | null = null;
  jsonData:       any[] = [];
  jsonDataLength: number | null = null;

  constructor(@Inject(MAT_DIALOG_DATA) public config: DialogConfig) {}

  uploadFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const reader = new FileReader();
    reader.readAsArrayBuffer(input.files[0]);

    reader.onload = (e: ProgressEvent<FileReader>) => {
      // e.target puede ser null - guard explícito
      if (!e.target?.result) return;

      const dataXLSX = e.target.result;
      const workBook = XLSX.read(dataXLSX, { type: 'binary' });
      const sheet1   = workBook.SheetNames[0];
      const sheet    = workBook.Sheets[sheet1];

      this.jsonData = this.readRows(sheet);
      // Las demás hojas viajan como propiedad NO enumerable del arreglo (no
      // cambia su forma para quien ya lo usa): p. ej. la hoja de correcciones
      // de nombres del archivo de Médicos.
      const sheets: Record<string, any[]> = {};
      for (const name of workBook.SheetNames) {
        sheets[name] = this.readRows(workBook.Sheets[name]);
      }
      Object.defineProperty(this.jsonData, 'sheets', { value: sheets, enumerable: false });

      // Construye representación tabular para uso interno (debug/log).
      const arrayData: any[][] = [];
      this.jsonData.forEach(element => {
        const row: any[] = [];
        Object.keys(element).map((r: string) => {
          row.push(element[r]);
        });
        arrayData.push(row);
      });

      this.jsonDataLength = this.jsonData.length;
    };
  }

  /**
   * Filas de una hoja como objetos, tomando como encabezados el primer
   * renglón con 2+ celdas llenas. Así se puede volver a cargar un Excel
   * descargado del catálogo, que trae arriba el título "Catálogo de …" en
   * un renglón propio (y un renglón en blanco) antes de los encabezados.
   */
  private readRows(sheet: XLSX.WorkSheet): any[] {
    const grid: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });
    const headerIndex = grid.findIndex(row =>
      (row ?? []).filter(cell => cell !== null && cell !== undefined && String(cell).trim() !== '').length >= 2);
    if (headerIndex <= 0) return XLSX.utils.sheet_to_json(sheet);
    const ref = sheet['!ref'];
    if (!ref) return [];
    const range = XLSX.utils.decode_range(ref);
    // Renglón real del encabezado (sheet_to_json con blankrows:false salta
    // renglones vacíos, así que se busca por contenido).
    for (let r = range.s.r; r <= range.e.r; r++) {
      const cells: any[] = [];
      for (let c = range.s.c; c <= range.e.c; c++) {
        const cell = sheet[XLSX.utils.encode_cell({ r, c })];
        if (cell && String(cell.v ?? '').trim() !== '') cells.push(cell.v);
      }
      if (cells.length >= 2) {
        return XLSX.utils.sheet_to_json(sheet, { range: { s: { r, c: range.s.c }, e: range.e } });
      }
    }
    return XLSX.utils.sheet_to_json(sheet);
  }

  async getJsonData(): Promise<void> {
    if (this.config) {
      const items: any[] = [];
      const keys:  string[] = [];
      let mapping: Record<string, MappingEntry> = {};

      for (const columns of this.config.structure ?? []) {
        keys.push(columns.columnName);
        mapping = {
          ...mapping,
          [columns.columnName]: { name: columns.columnKey, type: columns.type },
        };
      }

      for (const item of this.jsonData) {
        const row: Record<string, any> = {};
        for (const key of keys) {
          if (item[key] === undefined) {
            await this.customMessage.open({
              title:      'Información',
              type:       'neutral',
              message:    `El campo <b>${key}</b> no se encuentra en el excel, y es requerido.<br>` +
                          `Asegúrate de que no existan espacios dentro de la celda.`,
              dismissBtn: true,
              acceptBtn:  true,
            });
            return;
          }
          if (mapping[key].type === 'text') {
            row[mapping[key].name] = item[key].toString().trim().toUpperCase();
          }
        }
        items.push(row);
      }
      this.dialogRef.close(items);
    } else {
      this.dialogRef.close(this.jsonData);
    }
  }
}